import { useAuth } from "../../auth/AuthContext";

import logo from "../../assets/plumescript-logo-removebg.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBars, faTimes, faRightFromBracket, faRightToBracket, faAngleUp,
  faAngleDown, faSearch, faArrowLeft, faChevronUp, faChevronDown,
  faBell, faGear, faQuestionCircle, faEnvelope, faUser, faPuzzlePiece,
  faBookOpen, faComments, faPenNib, faPhone, faLightbulb } from "@fortawesome/free-solid-svg-icons";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect, useRef, useCallback } from "react";
import Search from "../Search/Search";
import ThemeSwitcher from "../../theme/ThemeSwitcher";
import LocaleSwitcher from "../../i18n/LocaleSwitcher";
import { useT } from "../../i18n/I18nContext";
import NotificationsPanel from "../Notifications/NotificationsPanel";
import GuestSignupBanner from "./GuestSignupBanner";
import { getUnreadNotificationsCount } from "../../util/notificationsApi";
import { getUnreadMessagesCount } from "../../util/messagesApi";
import { getAccessToken } from "../../util/auth";
import { avatarSrc } from "../../util/avatar";
import { profilePath } from "../../util/urlIds";

const Navigation = ({ searchQuery, setSearchQuery }) => {
  const t = useT();
  const location = useLocation();
  const navigate = useNavigate();
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);

  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const toggleSearchFocus = () => {
    setIsSearchFocused(!isSearchFocused);
  };

  const [isSearchVisible, setIsSearchVisible] = useState(false);
  const toggleSearch = () => {
    setIsSearchVisible(!isSearchVisible);
  };

  const toggleDropdown = () => {
    setIsDropdownVisible(!isDropdownVisible);
  };

  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const [ isProfileMenuOpen, setProfileMenuOpen ] = useState(false);
  const [isNotificationsOpen, setNotificationsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  // Aggregate unread-message count — separate poll from the
  // notifications-bell counter above. Drives a second badge over
  // the messages envelope icon (desktop iconBar + mobile sidebar
  // entry). Same 60 s polling cadence as notifications.
  const [messagesUnread, setMessagesUnread] = useState(0);

  const toggleProfileMenu = () => {
    setProfileMenuOpen(!isProfileMenuOpen);
  };

  const toggleSidebar = () => {
    setSidebarOpen(!isSidebarOpen);
  };

  const { user, status, signOut: authSignOut } = useAuth();
  const [username, setUsername] = useState("");
  const [_role, setRole] = useState("");
  const [picture, setPicture] = useState("");

  const dropdownRef = useRef(null);
  const notificationsRef = useRef(null);

  const handleLogout = async () => {
    setIsDropdownVisible(false);
    setSidebarOpen(false);
    try {
      await authSignOut();
    } finally {
      navigate("/", { replace: true });
    }
  };

  // Collapse every transient overlay whenever the route changes, so the
  // sidebar / profile dropdown / notifications / mobile search don't survive
  // a navigation.
  useEffect(() => {
    setSidebarOpen(false);
    setIsDropdownVisible(false);
    setProfileMenuOpen(false);
    setNotificationsOpen(false);
    setIsSearchVisible(false);
  }, [location.pathname]);

  // Escape closes whichever overlay is open.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      setSidebarOpen(false);
      setIsDropdownVisible(false);
      setNotificationsOpen(false);
      setIsSearchVisible(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Close the desktop profile dropdown and notifications popover when the
  // user clicks anywhere outside of them.
  useEffect(() => {
    if (!isDropdownVisible && !isNotificationsOpen) return undefined;
    const onDown = (e) => {
      if (
        isDropdownVisible &&
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target)
      ) {
        setIsDropdownVisible(false);
      }
      if (
        isNotificationsOpen &&
        notificationsRef.current &&
        !notificationsRef.current.contains(e.target)
      ) {
        setNotificationsOpen(false);
      }
    };
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, [isDropdownVisible, isNotificationsOpen]);

  // Single fetch of the two badge counts. Stable across renders so
  // the 60 s poll AND the ``wo:badges:refresh`` event listener below
  // can both call it without re-subscribing each tick. React state
  // can lag behind storage for one tick after a silent refresh fails
  // (the ``SESSION_CLEARED_EVENT`` listener flips status but the
  // current tick already scheduled this call) — re-check the token
  // at call-time so we don't fire a guaranteed 401 at the server.
  const refreshBadges = useCallback(async () => {
    if (!getAccessToken()) return;
    try {
      const n = await getUnreadNotificationsCount();
      setUnreadCount(n);
    } catch {
      /* backend may be unreachable; surface nothing */
    }
    try {
      const n = await getUnreadMessagesCount();
      setMessagesUnread(n);
    } catch {
      /* same — silent on backend reachability issues */
    }
  }, []);

  useEffect(() => {
    // Wait for AuthContext to confirm the stored token is still valid
    // (status === "signedIn" only flips after /api/auth/me/ succeeds).
    // Polling on a just-``user`` check fired 401s during the boot-time
    // verification window.
    if (status !== "signedIn" || !user) return undefined;
    refreshBadges();
    const id = window.setInterval(refreshBadges, 60000);
    return () => window.clearInterval(id);
  }, [user, status, refreshBadges]);

  // Instant badge refresh on read actions elsewhere in the app
  // (NotificationsPanel mark-read / mark-all, Messages thread open).
  // Without this the badge stayed stale for up to 60 s after the user
  // had clearly cleared the surface it was counting.
  useEffect(() => {
    if (!user) return undefined;
    const onRefresh = () => refreshBadges();
    window.addEventListener("wo:badges:refresh", onRefresh);
    return () => window.removeEventListener("wo:badges:refresh", onRefresh);
  }, [user, refreshBadges]);

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };
  // Mirror the Django-backed user profile into local state + sessionStorage so
  // legacy components that read ``sessionStorage.getItem("email")`` keep
  // working. Firebase / Firestore mirroring and the third-party geolocation
  // ping were removed when Firebase Auth was replaced with first-party JWT;
  // see MIGRATION.md if a location-saving side-effect is needed again.
  useEffect(() => {
    if (!user) {
      sessionStorage.removeItem("username");
      sessionStorage.removeItem("email");
      sessionStorage.removeItem("picture");
      return;
    }
    setUsername(user.username || "");
    setRole(user.role || "");
    setPicture(user.picture || "");
    if (user.username) sessionStorage.setItem("username", user.username);
    if (user.email) sessionStorage.setItem("email", user.email);
    if (user.picture) sessionStorage.setItem("picture", user.picture);
  }, [user]);
  return (
    <>
    <nav
      aria-label={t("a11y.primary_nav")}
      className="w-full bg-surface border-b border-[#EAEAEA] sticky top-0 z-50"
    >
      <div>
      {/* Hamburger Menu Button. ``top-2`` (was top-4) so the icon
          center lands ~31 px from the viewport top -- matches the
          centered logo's vertical centre on mobile (py-2 8 px +
          half of h-12 24 px = 32 px). The 16 px padding keeps the
          tap target ~48 px so the lift doesn't shrink it. */}
      <div className="fixed top-2 left-4 p-4 lg:hidden">
        <button
          onClick={toggleSidebar}
          aria-label={t("nav.open_menu")}
          aria-expanded={isSidebarOpen}
          aria-controls="mobile-sidebar"
        >
          <FontAwesomeIcon icon={faBars} className="text-text" />
        </button>
      </div>

      {/* Mobile sidebar.
          - Slides in from the left as a 280px column on a dim backdrop.
          - Header carries the brand logo + close button so the panel is
            self-identifying (was previously just an X with no context).
          - Sections are grouped (Public / Account / Preferences) with
            small uppercase kickers so the long list reads as a menu
            instead of a wall of links.
          - Theme + Locale switchers live at the bottom in a "Preferences"
            footer so mobile users can change colour mode + language
            without diving into Settings. */}
      {isSidebarOpen && (
        // Backdrop click closes the sidebar; Escape is handled by
        // the keydown listener above so keyboard users keep an exit.
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
        <div
          id="mobile-sidebar"
          role="dialog"
          aria-modal="true"
          aria-label={t("nav.open_menu")}
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 overflow-y-auto"
          onClick={(e) => {
            if (e.target === e.currentTarget) setSidebarOpen(false);
          }}
        >
          <aside className="bg-surface w-72 max-w-[85vw] min-h-full flex flex-col shadow-xl">
            {/* Header: logo + close. Logo doubles as a Home link so a
                tap on the brand mark dismisses the panel and goes back
                to the landing — same affordance as the desktop navbar. */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <Link
                to="/"
                onClick={() => setSidebarOpen(false)}
                className="flex items-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 rounded-md"
                aria-label="Plumescript"
              >
                <img
                  src={logo}
                  alt="Plumescript"
                  className="app-logo h-9 w-auto object-contain"
                />
              </Link>
              <button
                type="button"
                onClick={toggleSidebar}
                aria-label={t("nav.close_menu")}
                className="w-9 h-9 inline-flex items-center justify-center rounded-md text-secondText hover:bg-surfaceAlt hover:text-text transition focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            {/* Body. Single-pane menu (the prior [Menu | Notifications]
                tab strip + inline notifications view was removed —
                the mobile separate view felt off and duplicated the
                floating NotificationsPanel that the in-menu
                "Notifications" entry now opens directly). */}
            <div className="flex-1 flex flex-col min-h-0">
            <div className="flex-1 flex flex-col">
            {/* Account block — only when the user is signed in. The
                profile link toggles a nested submenu that holds the
                authenticated-only routes; logout sits at the bottom. */}
            {user && (
              <div className="px-4 pt-4 pb-2">
                <p className="text-[10px] uppercase tracking-[0.12em] font-semibold text-secondText mb-2">
                  {t("nav.profile_menu")}
                </p>
                <button
                  type="button"
                  className="w-full text-text text-left flex items-center justify-between py-2 rounded-md hover:bg-surfaceAlt px-2"
                  onClick={toggleProfileMenu}
                  aria-expanded={isProfileMenuOpen}
                  aria-controls="mobile-profile-menu"
                >
                  <span className="flex items-center gap-3">
                    <FontAwesomeIcon icon={faUser} aria-hidden="true" className="text-secondText w-4" />
                    {t("nav.profile")}
                  </span>
                  <FontAwesomeIcon
                    icon={isProfileMenuOpen ? faAngleUp : faAngleDown}
                    aria-hidden="true"
                    className="text-secondText"
                  />
                </button>

                {isProfileMenuOpen && (
                  <div
                    id="mobile-profile-menu"
                    className="mt-1 pl-7 text-sm text-text space-y-1"
                  >
                    <Link to={profilePath(user)} className="block py-1.5 hover:text-primary">
                      {t("nav.view_profile")}
                    </Link>
                    <Link to="/collaboration" className="block py-1.5 hover:text-primary">
                      {t("nav.overview")}
                    </Link>
                    <Link to="/work" className="block py-1.5 hover:text-primary">
                      {t("nav.manage_work")}
                    </Link>
                    <Link to="/goals" className="block py-1.5 hover:text-primary">
                      {t("nav.goals")}
                    </Link>
                    <button
                      type="button"
                      onClick={handleLogout}
                      className="block w-full text-left py-1.5 text-danger hover:underline"
                    >
                      {t("nav.logout")}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Public block — visible to everyone. Same routes as the
                desktop public quick-access row, with their FontAwesome
                icons. */}
            <div className="px-4 pt-3 pb-2">
              <nav className="flex flex-col gap-1 text-text" aria-label={t("a11y.primary_nav")}>
                <Link to="/browse" className="flex items-center gap-3 py-2 px-2 rounded-md hover:bg-surfaceAlt">
                  <FontAwesomeIcon icon={faBookOpen} aria-hidden="true" className="text-secondText w-4" />
                  {t("nav.browse")}
                </Link>
                <Link to="/forum" className="flex items-center gap-3 py-2 px-2 rounded-md hover:bg-surfaceAlt">
                  <FontAwesomeIcon icon={faComments} aria-hidden="true" className="text-secondText w-4" />
                  {t("nav.forum")}
                </Link>
                {/* /editor is the EditorAccess guest-aware shim — signed-in
                    writers reach the editor via "Manage work" → project →
                    chapter, so duplicating it here just adds a dead-end. */}
                {!user && (
                  <Link to="/editor" className="flex items-center gap-3 py-2 px-2 rounded-md hover:bg-surfaceAlt">
                    <FontAwesomeIcon icon={faPenNib} aria-hidden="true" className="text-secondText w-4" />
                    {t("nav.editor")}
                  </Link>
                )}
                <Link to="/games" className="flex items-center gap-3 py-2 px-2 rounded-md hover:bg-surfaceAlt">
                  <FontAwesomeIcon icon={faPuzzlePiece} aria-hidden="true" className="text-secondText w-4" />
                  {t("nav.games")}
                </Link>
                <Link to="/curiosities" className="flex items-center gap-3 py-2 px-2 rounded-md hover:bg-surfaceAlt">
                  <FontAwesomeIcon icon={faLightbulb} aria-hidden="true" className="text-secondText w-4" />
                  {t("nav.curiosities")}
                </Link>
                <Link to="/contact" className="flex items-center gap-3 py-2 px-2 rounded-md hover:bg-surfaceAlt">
                  <FontAwesomeIcon icon={faPhone} aria-hidden="true" className="text-secondText w-4" />
                  {t("nav.contact")}
                </Link>
                <Link to="/help" className="flex items-center gap-3 py-2 px-2 rounded-md hover:bg-surfaceAlt">
                  <FontAwesomeIcon icon={faQuestionCircle} aria-hidden="true" className="text-secondText w-4" />
                  {t("nav.help")}
                </Link>
                {user && (
                  <Link to="/message" className="flex items-center gap-3 py-2 px-2 rounded-md hover:bg-surfaceAlt">
                    <FontAwesomeIcon icon={faEnvelope} aria-hidden="true" className="text-secondText w-4" />
                    <span className="flex-1 text-left">{t("nav.messages")}</span>
                    {/* Inline pill to mirror the notifications-bell
                        unread chip below (lines ~372). Same red
                        accent + same 9+ collapse so the mobile
                        sidebar reads consistently across both
                        unread surfaces. */}
                    {messagesUnread > 0 ? (
                      <span className="ml-2 inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-danger px-1.5 text-[10px] font-bold text-white">
                        {messagesUnread > 9 ? "9+" : messagesUnread}
                      </span>
                    ) : null}
                  </Link>
                )}
                {user && (
                  // Discrete notifications entry — matches the
                  // sibling /message link style. Tap closes the
                  // mobile sidebar and opens the same floating
                  // NotificationsPanel the desktop bell uses, so
                  // the experience is one panel across viewports
                  // instead of a mobile-only inline view that
                  // duplicated the same data with different chrome.
                  <button
                    type="button"
                    onClick={() => {
                      setSidebarOpen(false);
                      setNotificationsOpen(true);
                    }}
                    className="flex items-center gap-3 py-2 px-2 rounded-md hover:bg-surfaceAlt text-text"
                  >
                    <FontAwesomeIcon icon={faBell} aria-hidden="true" className="text-secondText w-4" />
                    <span className="flex-1 text-left">{t("nav.notifications")}</span>
                    {unreadCount > 0 ? (
                      <span className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-danger px-1.5 text-[10px] font-bold text-white">
                        {unreadCount > 9 ? "9+" : unreadCount}
                      </span>
                    ) : null}
                  </button>
                )}
                {user && (
                  <Link to="/setting" className="flex items-center gap-3 py-2 px-2 rounded-md hover:bg-surfaceAlt">
                    <FontAwesomeIcon icon={faGear} aria-hidden="true" className="text-secondText w-4" />
                    {t("nav.settings")}
                  </Link>
                )}
              </nav>
            </div>

            {/* Preferences footer — theme + locale switchers, plus the
                guest CTA pair if not signed in. Pushed to the bottom
                with mt-auto so it sticks to the panel's foot regardless
                of how short the menu list is. */}
            <div className="mt-auto border-t border-border px-4 py-4 space-y-3">
              {!user ? (
                <div className="flex gap-2">
                  <Link
                    to="/login"
                    className="flex-1 inline-flex items-center justify-center gap-2 rounded-md border border-border bg-surface text-text px-3 py-2 text-sm font-medium hover:bg-surfaceAlt"
                  >
                    <FontAwesomeIcon icon={faRightToBracket} aria-hidden="true" />
                    {t("nav.login")}
                  </Link>
                  <Link
                    to="/register"
                    className="flex-1 inline-flex items-center justify-center gap-2 rounded-md bg-primary text-white px-3 py-2 text-sm font-medium hover:brightness-110"
                  >
                    {t("nav.register")}
                  </Link>
                </div>
              ) : null}

              <div className="flex items-center justify-between gap-2">
                <span className="text-[10px] uppercase tracking-[0.12em] font-semibold text-secondText">
                  {t("settings.appearance.title")}
                </span>
                <div className="flex items-center gap-1">
                  <ThemeSwitcher compact />
                  <LocaleSwitcher compact />
                </div>
              </div>
            </div>
            </div>
            </div>
          </aside>
        </div>
      )}
      {/* Notifications panel — opened from the desktop bell OR the
          mobile sidebar's in-menu Notifications entry. Mounted at
          the navbar root so it floats on top of (not inside) the
          sidebar; the mobile entry closes the sidebar first so the
          panel isn't hidden behind it. Conditional render keeps
          it out of the DOM (and skips its data fetch) when
          nobody's looking at it. */}
      {isNotificationsOpen ? (
        <NotificationsPanel
          onClose={() => setNotificationsOpen(false)}
        />
      ) : null}
    </div>
      <div className="md:h-[72px] px-4 md:px-8 py-2 flex flex-col md:flex-row items-center gap-4 md:gap-6
      max-md:justify-center">
        {/* Logo. md:h-14 keeps the IMG comfortably inside the
            72 px navbar (py-2 leaves a 56 px content band, exactly
            matching h-14). The ``.app-logo`` rule in index.css no
            longer applies the translateY(9.5%) push-down that used
            to overflow the IMG below the navbar bottom edge — see
            the comment block on ``.app-logo`` there for why the
            transform was removed. */}
        <div className="max-md:text-center shrink-0">
          <Link to="/">
            <img
              src={logo}
              alt="Plumescript"
              className="app-logo h-12 md:h-14 w-auto object-contain"
            />
          </Link>
        </div>

        {/* Search bar — uses a guest-tailored placeholder when no
            user. Signed-in writers also have their own work,
            messages, and goals indexed; guests only have public
            catalogue + writers, so the copy makes the actual
            scope explicit. ``max-w-md`` caps the resting width
            so the search doesn't dominate the row; on focus the
            cap drops so the input grows into the space the
            hidden navbar icons leave behind. */}
        <div
          className={`hidden lg:flex relative flex-1 min-w-0 items-center transition-all duration-200 ${
            isSearchFocused ? "" : "max-w-md"
          }`}
        >
          <input
            type="search"
            name="search"
            id="search"
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder={
              user
                ? t("nav.search.placeholder")
                : t("nav.search.placeholder.guest")
            }
            aria-label={
              user
                ? t("nav.search.placeholder")
                : t("nav.search.placeholder.guest")
            }
            onFocus={toggleSearchFocus}
            onBlur={toggleSearchFocus}
            className="appearance-none border border-border bg-surface text-text placeholder:text-secondText
            hover:border-primary/40 transition-colors rounded-full
            w-full py-2.5 pl-11 pr-4 text-sm
            focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/40"
          />
          <div className="absolute left-4 flex items-center pointer-events-none text-secondText">
            <FontAwesomeIcon icon={faSearch} className="text-[14px]" aria-hidden="true" />
          </div>
        </div>

        {/* Search Bar Button. ``top-2`` matches the hamburger
            lift so both fixed icons sit at the same y as the
            centered logo (see hamburger comment for the math). */}
      {!isSearchVisible && (
      <div className="fixed top-2 right-4 p-4 lg:hidden">
        <button onClick={toggleSearch} aria-label={t("nav.open_search")}>
          <FontAwesomeIcon icon={faSearch} className="text-text" />
        </button>
      </div>
      )}

      {/* Search Bar (mobile overlay).
          - Click on the dim backdrop dismisses (was previously trapped:
            only the small back arrow worked, which is hard to hit and
            non-obvious).
          - ``Escape`` also dismisses (handled inline on the input — the
            overlay is only visible while the input is focused, so a
            local keyDown is enough; no global listener leak).
          - Wrapping in a <form> lets ``Enter`` close the overlay so the
            search results page (driven by the parent's ``searchQuery``
            state) is what the user actually lands on. */}
      {isSearchVisible && (
        // Backdrop dismiss; the input handles its own keyboard
        // (Enter submits the search form below; Escape via the
        // global keydown listener closes the overlay).
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
        <div
          className="fixed inset-0 bg-surface/95 backdrop-blur-sm z-50"
          onClick={(e) => {
            if (e.target === e.currentTarget) setIsSearchVisible(false);
          }}
        >
          <form
            // Position with ``inset-x-4 top-4`` instead of ``w-full m-4``.
            // ``w-full + margin`` on a flex item inside an inset-0
            // container made the form 100vw + 2rem wide — pushing the
            // input + back arrow off the right edge on small screens.
            // Insets give a clean 1rem gap on every side.
            className="absolute inset-x-4 top-4 flex items-center"
            onSubmit={(e) => {
              e.preventDefault();
              setIsSearchVisible(false);
            }}
          >
            <input
              type="search"
              name="search"
              id="search-mobile"
              value={searchQuery}
              onChange={handleSearchChange}
              onKeyDown={(e) => {
                if (e.key === "Escape") setIsSearchVisible(false);
              }}
              placeholder={
                user
                  ? t("nav.search.placeholder")
                  : t("nav.search.placeholder.guest")
              }
              aria-label={
                user
                  ? t("nav.search.placeholder")
                  : t("nav.search.placeholder.guest")
              }
              className="appearance-none min-w-0 flex-1 border border-border bg-surface text-text placeholder:text-secondText hover:border-primary transition-colors rounded-full py-3 pl-12 pr-4 text-base focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
              // eslint-disable-next-line jsx-a11y/no-autofocus -- focus is intentional on this modal/form's primary input
              autoFocus
            />
            <div className="absolute pl-6 flex items-center">
              <button
                type="button"
                onClick={() => setIsSearchVisible(false)}
                aria-label={t("nav.close_search")}
                title={t("nav.close_search")}
                className="text-secondText hover:text-text transition"
              >
                <FontAwesomeIcon icon={faArrowLeft} />
              </button>
            </div>
          </form>
        </div>
      )}
        
        {/* Icon container */}
        {user ? (
        <>
            {!isSearchFocused && (
            <div className="hidden lg:flex gap-1 items-center shrink-0">
          <LocaleSwitcher compact />
          <ThemeSwitcher compact />
          <div className="relative" ref={notificationsRef}>
            <button
              type="button"
              onClick={() => setNotificationsOpen((v) => !v)}
              className={`relative inline-flex h-9 w-9 items-center justify-center rounded-full transition ${
                isNotificationsOpen
                  ? "bg-primaryMuted text-primary"
                  : "text-text hover:bg-surfaceAlt hover:text-primary"
              }`}
              aria-label={t("nav.notifications")}
              aria-haspopup="dialog"
              aria-expanded={isNotificationsOpen}
            >
              <FontAwesomeIcon icon={faBell} />
              {unreadCount > 0 ? (
                <span className="absolute right-0.5 top-0.5 inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-danger px-1 text-[10px] font-bold text-white">
                  {unreadCount > 9 ? "9+" : unreadCount}
                </span>
              ) : null}
            </button>
            {/* NotificationsPanel itself renders at the navbar top
                level (further down) so both the desktop bell and
                the mobile-menu trigger can open it without the
                ``hidden lg:flex`` wrapper clipping fixed children
                via display:none. */}
          </div>
          {/* Active-route awareness: when the writer is already
              on /message, /setting, or /help, the matching icon
              picks up the theme's primary tint + muted-pill bg
              so the navbar reflects "where am I" instead of
              rendering a row of indistinguishable static glyphs.
              ``text-text hover:text-primary`` alone wasn't doing
              that — every icon looked identical regardless of
              route. */}
          {[
            { to: "/games", icon: faPuzzlePiece, label: t("nav.games") },
            { to: "/curiosities", icon: faLightbulb, label: t("nav.curiosities") },
            // ``badgeCount`` drives a small red pip on the messages
            // icon when the writer has unread DMs. Mirrors the
            // notifications-bell badge pattern at lines ~600-602.
            { to: "/message", icon: faEnvelope, label: t("nav.messages"), badgeCount: messagesUnread },
            { to: "/setting", icon: faGear, label: t("nav.settings") },
            { to: "/contact", icon: faPhone, label: t("nav.contact") },
            { to: "/help", icon: faQuestionCircle, label: t("nav.help") },
          ].map(({ to, icon, label, badgeCount }) => {
            const isActive = location.pathname.startsWith(to);
            return (
              <Link
                key={to}
                to={to}
                aria-label={label}
                aria-current={isActive ? "page" : undefined}
                className={`relative inline-flex h-9 w-9 items-center justify-center rounded-full transition ${
                  isActive
                    ? "bg-primaryMuted text-primary"
                    : "text-text hover:bg-surfaceAlt hover:text-primary"
                }`}
              >
                <FontAwesomeIcon icon={icon} />
                {badgeCount && badgeCount > 0 ? (
                  <span className="absolute right-0.5 top-0.5 inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-danger px-1 text-[10px] font-bold text-white">
                    {badgeCount > 9 ? "9+" : badgeCount}
                  </span>
                ) : null}
              </Link>
            );
          })}
            </div>
            )}
        {/* User info */}
            <div className="hidden lg:flex items-center relative shrink-0" ref={dropdownRef}>
          <button
            className="flex items-center gap-3 pl-1.5 pr-5 py-1.5 min-w-[14rem] border border-border bg-surface rounded-full hover:bg-surfaceAlt focus:outline-none focus:ring-2 focus:ring-primary/30 transition"
            type="button"
            onClick={toggleDropdown}
            aria-haspopup="menu"
            aria-expanded={isDropdownVisible}
            aria-controls="dropdownAvatarName"
            aria-label={t("nav.profile_menu")}
          >
            <img
                  src={avatarSrc(picture)}
                  alt="" aria-hidden="true" className="h-12 w-12 rounded-full" />
            <span className="flex-1 text-left text-base font-semibold text-text truncate">
              {username}
            </span>
            <FontAwesomeIcon
              icon={isDropdownVisible ? faChevronUp : faChevronDown}
              className="text-secondText text-[12px]"
              aria-hidden="true"
            />
          </button>
          {isDropdownVisible && (
            <div
              id="dropdownAvatarName"
              role="menu"
              className="z-20 absolute top-full right-0 mt-2 min-w-[12rem] bg-surface border border-border
              divide-y divide-border rounded-lg shadow-md"
            >
              <div className="px-4 py-3 text-sm text-text ">
                <div className="font-medium ">
                  <Link to="/collaboration" role="menuitem">{t("nav.overview")}</Link>
                </div>
              </div>
              <ul className="py-2 text-sm text-text ">
                <li>
                  {/* Self-profile shortcut. ``profilePath`` prefers
                      the username so the URL stays clean
                      (/profile/<username>) instead of leaking the
                      email. Falls back to ``/profile`` when the
                      live user object isn't loaded yet, in which
                      case the route's empty-segment branch sends
                      the user to its own 404 / login flow. */}
                  <Link
                    to={profilePath(user)}
                    role="menuitem"
                    className="block px-4 py-2 hover:bg-surfaceAlt "
                  >
                    <FontAwesomeIcon icon={faUser} className="mr-2 text-secondText" />
                    {t("nav.view_profile")}
                  </Link>
                </li>
                <li>
                  <Link
                    to="/browse"
                    role="menuitem"
                    className="block px-4 py-2 hover:bg-surfaceAlt "
                  >
                    {t("nav.browse")}
                  </Link>
                </li>
                <li>
                  <Link
                    to="/work"
                    role="menuitem"
                    className="block px-4 py-2 hover:bg-surfaceAlt "
                  >
                    {t("nav.manage_work")}
                  </Link>
                </li>
                <li>
                  <Link
                    to="/forum"
                    role="menuitem"
                    className="block px-4 py-2 hover:bg-surfaceAlt "
                  >
                    {t("nav.forum")}
                  </Link>
                </li>
                <li>
                  <Link
                    to="/goals"
                    role="menuitem"
                    className="block px-4 py-2 hover:bg-surfaceAlt "
                  >
                    {t("nav.goals")}
                  </Link>
                </li>
                <li>
                  <Link
                    to="/setting"
                    role="menuitem"
                    className="block px-4 py-2 hover:bg-surfaceAlt "
                  >
                    {t("nav.settings")}
                  </Link>
                </li>
              </ul>
              <div className="py-2">
                <button
                  type="button"
                  role="menuitem"
                  onClick={handleLogout}
                  className="block w-full text-left px-4 py-2 text-sm text-text hover:bg-surfaceAlt"
                >
                  {t("nav.logout")}
                </button>
              </div>
            </div>
          )}
            </div>
        </>
        ) : (
          // Two clusters on the guest path: the icon row (locale,
          // theme, 5 quick-access links) collapses on search-focus
          // so the search can expand into that space; the auth
          // CTAs (Login + Register) stay visible because losing
          // the only sign-in / sign-up entry on focus would feel
          // broken — guests need a constant path to convert.
          <div className="hidden lg:flex items-center gap-1 shrink-0">
            {!isSearchFocused && (
              <>
                <LocaleSwitcher compact />
                <ThemeSwitcher compact />
                {/* Public-only quick-access row. Mirrors the icon
                    list on the signed-in side for routes that
                    actually work without auth so guests can reach
                    the catalogue, the forum, the editor preview,
                    the games, and the help centre straight from
                    the navbar instead of fishing in the mobile
                    sidebar. ``/editor`` lands on the guest-aware
                    ``EditorAccess`` shim — it doesn't attempt to
                    load a real document. */}
                {[
                  { to: "/browse", icon: faBookOpen, label: t("nav.browse") },
                  { to: "/forum", icon: faComments, label: t("nav.forum") },
                  { to: "/editor", icon: faPenNib, label: t("nav.editor") },
                  { to: "/games", icon: faPuzzlePiece, label: t("nav.games") },
                  { to: "/curiosities", icon: faLightbulb, label: t("nav.curiosities") },
                  { to: "/contact", icon: faPhone, label: t("nav.contact") },
                  { to: "/help", icon: faQuestionCircle, label: t("nav.help") },
                ].map(({ to, icon, label }) => {
                  const isActive = location.pathname.startsWith(to);
                  return (
                    <Link
                      key={to}
                      to={to}
                      aria-label={label}
                      aria-current={isActive ? "page" : undefined}
                      className={`inline-flex h-9 w-9 items-center justify-center rounded-full transition ${
                        isActive
                          ? "bg-primaryMuted text-primary"
                          : "text-text hover:bg-surfaceAlt hover:text-primary"
                      }`}
                    >
                      <FontAwesomeIcon icon={icon} />
                    </Link>
                  );
                })}
              </>
            )}
            <Link
              to="/login"
              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-semibold text-text rounded-full hover:bg-surfaceAlt transition"
            >
              <FontAwesomeIcon icon={faRightToBracket} />
              {t("nav.login")}
            </Link>
            <Link
              to="/register"
              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-semibold text-white bg-primary rounded-full hover:brightness-110 transition"
            >
              <FontAwesomeIcon icon={faRightFromBracket} />
              {t("nav.register")}
            </Link>
          </div>
        )}
      </div>
    </nav>
    {/* Sign-up nudge for anonymous visitors. Lives just below the
        navbar so every public page picks it up automatically — and
        no-ops (returns null) once the visitor signs in or
        dismisses it. */}
    <GuestSignupBanner />
    {/* Search Results */}
    {searchQuery && (
      <Search searchQuery={searchQuery} />
      )}
    </>
  );
};

export default Navigation;
